import { createNode } from './createNode'
import { ELEVATOR_LEVELS } from './elevatorMotion'

// Fixed authoring markers: one call and one floor controller at each landing.
export class ElevatorXR {
  constructor(world, elevator) {
    this.world = world
    this.elevator = elevator
    this.panels = []
    this.boards = ELEVATOR_LEVELS.map((_, landing) => {
      const marker = elevator.uiMarkers?.get(`elevator_panel_${landing}`)
      if (!marker) return null
      const panel = this.panel(380, 210, marker)
      const title = this.text('Elevator', 24)
      panel.add(title)
      const row = createNode('uiview', { flexDirection: 'row', gap: 8, justifyContent: 'center' })
      const floors = ELEVATOR_LEVELS.map((_, floor) => {
        const button = this.text(String(floor), 30, () => {
          const status = elevator.status
          if (status.aboard && !status.occupied && !status.moving && status.floor !== floor) elevator.select(floor)
        })
        button.padding = 12
        button.flexGrow = 1
        row.add(button)
        return button
      })
      panel.add(row)
      const course = this.text('Course Room', 24, () => elevator.goToCourseRoom())
      course.padding = 10
      course.backgroundColor = '#344257'
      panel.add(course)
      return { panel, title, floors, landing }
    }).filter(Boolean)
    this.calls = ELEVATOR_LEVELS.map((_, floor) => {
      const marker = elevator.uiMarkers?.get(`elevator_call_${floor}`)
      if (!marker) return null
      const panel = this.panel(380, 100, marker)
      const text = this.text('Call elevator', 24, () => {
        if (!elevator.status.aboard && elevator.status.callFloor === floor) elevator.call()
      })
      panel.add(text)
      return { panel, text, floor }
    }).filter(Boolean)
  }

  panel(width, height, marker) {
    const panel = createNode('ui', { width, height, size: 0.002, transparent: false, backgroundColor: '#17202e', padding: 12, gap: 10, justifyContent: 'center', doubleside: false })
    // Copy world pose once: the panel stays on its landing, never on the body.
    marker.matrixWorld.decompose(panel.position, panel.quaternion, panel.scale)
    panel.scale.set(1, 1, 1)
    this.panels.push(panel)
    return panel
  }

  text(value, fontSize, action) {
    return createNode('uitext', { value, fontSize, color: '#ffffff', textAlign: 'center', padding: 4,
      ...(action ? { cursor: 'pointer', onPointerDown: e => e.stopPropagation(), onPointerUp: () => {
        if (this.visible()) action()
      } } : {}) })
  }

  visible() {
    return !!this.world.xr.session && !this.world.receptionist?.talking && !this.world.ui.state.pane &&
      !this.world.courseRoom?.state.active && !this.world.courseRoom?.graphOpen
  }

  show(panel, visible) {
    if (visible && !panel.mounted) {
      panel.activate({ world: this.world })
      panel.material.depthWrite = true
    } else if (!visible && panel.mounted) {
      this.world.pointer?.cancelForNode(panel)
      panel.deactivate()
    }
  }

  update() {
    const status = this.elevator.status
    const visible = this.visible()
    for (const { panel, title, floors, landing } of this.boards) {
      title.value = status.moving ? `Going to floor ${status.floor}` : status.occupied ? 'Occupied' : `Elevator · Floor ${status.floor}`
      floors.forEach((button, floor) => {
        const disabled = status.occupied || status.moving || status.floor === floor
        button.color = disabled ? '#a0aec0' : '#ffffff'
        button.backgroundColor = status.floor === floor ? '#285b8d' : disabled ? '#253044' : '#344257'
        button.cursor = disabled ? 'default' : 'pointer'
      })
      // Show the fixed controller when the platform is stopped at this floor.
      this.show(panel, visible && status.aboard && !status.moving && status.floor === landing)
    }
    for (const { panel, text, floor } of this.calls) {
      const disabled = status.occupied || status.moving || status.recallWait > 0
      text.value = status.moving ? 'Elevator moving' : status.occupied ? 'Occupied' : status.recallWait > 0 ? `Wait ${status.recallWait} s` : `Call elevator · Floor ${floor}`
      text.color = disabled ? '#a0aec0' : '#ffffff'
      text.cursor = disabled ? 'default' : 'pointer'
      this.show(panel, visible && !status.aboard && status.callFloor === floor)
    }
  }

  destroy() {
    for (const panel of this.panels) this.show(panel, false)
    this.panels.length = 0
  }
}
